import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  BookOpen, Heart, Eye, Clock, Hash, ArrowLeft, Trash2, Loader2, AlertTriangle, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { VerifiedBadge } from "../components/VerifiedBadge";

export default function StoryReader() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liking, setLiking] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/stories/${id}`)
      .then(({ data }) => setStory(data))
      .catch((err) => { toast.error(formatApiError(err.response?.data?.detail) || "Story not found"); nav("/stories"); })
      .finally(() => setLoading(false));
  }, [id, nav]);

  const like = async () => {
    if (!user) { nav("/login"); return; }
    setLiking(true);
    try {
      const { data } = await api.post(`/stories/${id}/like`);
      setStory((s) => ({ ...s, liked: data.liked, likes_count: data.likes_count }));
    } catch { /* noop */ } finally { setLiking(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this story? This can't be undone.")) return;
    try {
      await api.delete(`/stories/${id}`);
      toast.success("Story deleted");
      nav("/stories");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const approve = async () => {
    try { await api.post(`/admin/stories/${id}/approve`); toast.success("Story approved & published"); setStory((s) => ({ ...s, status: "published" })); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-[#00E5FF]" size={32} /></div>;
  if (!story) return null;

  const canManage = user && (user.id === story.author_id || user.role === "admin" || user.role === "moderator");
  const isStaff = user && (user.role === "admin" || user.role === "moderator");
  const paragraphs = (story.text_content || "").split(/\n\s*\n|\n/).filter((p) => p.trim());

  return (
    <div className="max-w-[760px] mx-auto px-4 sm:px-6 pt-14 pb-24" data-testid="story-reader-page">
      <button onClick={() => nav("/stories")} className="flex items-center gap-2 text-[#8A8D91] hover:text-white text-sm mb-6 transition-colors">
        <ArrowLeft size={16} /> Back to stories
      </button>

      {story.status === "review" && (
        <div className="flex items-center gap-2 bg-[#F1A817]/10 border border-[#F1A817]/30 text-[#F1A817] rounded-xl px-4 py-3 mb-6 text-sm" data-testid="story-review-banner">
          <AlertTriangle size={16} /> This story is awaiting moderator review and is not public yet.
          {isStaff && <button data-testid="story-approve-inline" onClick={approve} className="ml-auto flex items-center gap-1 text-[#31A24C] hover:underline font-semibold"><ShieldCheck size={14} /> Approve</button>}
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <span className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#00E5FF]/12 border border-[#00E5FF]/25">
          <BookOpen size={20} className="text-[#00E5FF]" />
        </span>
        {story.category && (
          <span className="text-[10px] font-mono uppercase tracking-wider text-[#00E5FF] bg-[#00E5FF]/10 border border-[#00E5FF]/25 px-2.5 py-1 rounded-full">{story.category}</span>
        )}
      </div>

      <h1 data-testid="story-title" className="font-heading text-3xl sm:text-4xl uppercase tracking-tight text-white leading-tight">{story.title}</h1>

      <div className="flex flex-wrap items-center gap-4 mt-4 text-[#8A8D91] font-mono text-xs">
        <Link to={`/u/${story.author_username}`} className="text-[#00E5FF] hover:underline flex items-center gap-1">@{story.author_username}<VerifiedBadge show={story.author_verified} size={13} testId="story-author-verified" /></Link>
        <span className="flex items-center gap-1"><Clock size={13} /> {story.reading_minutes} min read</span>
        <span className="flex items-center gap-1"><Eye size={13} /> {story.views} views</span>
        <span>{story.word_count} words</span>
      </div>

      {story.summary && (
        <p className="text-[#B0B3B8] text-base italic mt-5 border-l-2 border-[#00E5FF]/40 pl-4 leading-relaxed">{story.summary}</p>
      )}

      {story.tags?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-5" data-testid="story-tags">
          {story.tags.map((t) => (
            <span key={t} className="flex items-center gap-0.5 text-[11px] text-[#8A8D91] bg-white/[0.04] border border-white/[0.06] px-2.5 py-1 rounded-full"><Hash size={10} className="text-[#4599FF]" />{t}</span>
          ))}
        </div>
      )}

      <div className="h-px bg-[#1C1C1F] my-8" />

      <article data-testid="story-body" className="text-[#E4E6EB] text-[17px] leading-[1.85]">
        {paragraphs.map((p, i) => <p key={i} className="mb-5 whitespace-pre-wrap">{p}</p>)}
      </article>

      <div className="sticky bottom-4 mt-10 flex items-center gap-3 bg-[#0A0A0A]/90 backdrop-blur-md border border-white/[0.08] rounded-2xl px-4 py-3 shadow-[0_10px_40px_rgba(0,0,0,0.6)]">
        <button
          data-testid="story-like-btn"
          onClick={like}
          disabled={liking}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-colors ${story.liked ? "bg-[#E60023]/15 text-[#E60023] border border-[#E60023]/40" : "bg-white/[0.04] text-white border border-white/[0.08] hover:border-[#E60023]/40"}`}
        >
          <Heart size={16} fill={story.liked ? "#E60023" : "none"} /> {story.likes_count}
        </button>
        <div className="flex-1" />
        {canManage && (
          <button data-testid="story-delete-btn" onClick={del} className="flex items-center gap-2 px-4 py-2 rounded-xl text-[#E60023] hover:bg-[#E60023]/10 border border-transparent hover:border-[#E60023]/30 text-sm font-semibold transition-colors">
            <Trash2 size={16} /> Delete
          </button>
        )}
      </div>
    </div>
  );
}
